import React, { useEffect, useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import {
  AuditLogDTO,
  CourseDTO,
  CourseTypeDTO,
  MemberDTO,
  MemberTypeDTO,
  RplDTO,
  RplStatusDTO,
} from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';
import SimpleAlert from '../common/SimpleAlert';
import { useCourseTypes } from '../../hooks/useCourseTypeApi';
import { CourseTypeConstants } from '../../constants/CourseTypeConstants';
import { getMemberId, getRoles } from '../../utils/tokenUtils';
import ImageLink from '../common/ImageLink';
import FileUpload from '../common/FileUpload';
import { useFileApi } from '../../hooks/useFileApi';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import AuditLogTable from '../tables/AuditLogTable';
import { useAuditLogApi } from '../../hooks/useAuditLogApi';
import { useMemberTypes } from '../../hooks/useMemberTypeApi';
import { useMemberCategories } from '../../hooks/useMemberCategoryApi';
import { usePaymentTypes } from '../../hooks/usePaymentTypeApi';
import { useRplStatuses } from '../../hooks/useRplStatusApi';
import { useMemberApi } from '../../hooks/useMemberApi';
import { MemberCategoryConstants } from '../../constants/MemberCategoryConstants';
import { RplStatusConstants } from '../../constants/RplStatusConstants';
import { CourseStatusConstants } from '../../constants/CourseStatusConstants';
import { useRplApi } from '../../hooks/useRplApi';
import { useCourseApi } from '../../hooks/useCourseApi';
import { formatDateForInput } from '../../utils/dateUtils/formatDateForInput';
import FeedbackForm from './FeedbackForm';
import { ComponentConstants } from '../../constants/ComponentConstants';
import MemberDetails from '../common/MemberDetails';
import { FileTypeConstants } from '../../constants/FileTypeConstants';

interface RplFormProps {
  initialValues: RplDTO;
  onSubmit: (data: RplDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const RplForm: React.FC<RplFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<RplDTO>(initialValues);
  const { getFeedback } = useRplApi();

  // Feedback HTML is stored in blob storage now, not returned inline with the Rpl DTO — fetch it on demand.
  useEffect(() => {
    if (!formData.id) return;
    getFeedback(formData.id)
      .then((feedback) => setFormData((prev) => ({ ...prev, feedback })))
      .catch(() => { });
  }, [formData.id]);

  const memberId = getMemberId();

  const statusFieldName = 'rplStatusId';

  const roles = getRoles();
  const FORM_SECTIONS = {
    ...(roles?.includes('Admin')
      ? {
        memberDetails: {
          id: 'member-details',
          title: 'Member Details',
        },
        userDetails: {
          id: 'user-details',
          title: 'User Details',
        },
        recordDetails: {
          id: 'record-details',
          title: 'Record Details',
        },
        paymentDetails: {
          id: 'payment-details',
          title: 'Payment Details',
        },
        feedback: {
          id: 'feedback',
          title: 'Feedback',
        },
        history: {
          id: 'history',
          title: 'History',
        },
      }
      : {}),
    applicationDetails: {
      id: 'application-details',
      title: 'Application Details',
    },
  } as const;

  const { uploadFile, getFileUrl } = useFileApi();

  const [uploadingFiles, setUploadingFiles] = useState<{
    [key: string]: boolean;
  }>({});


  const { search: searchCourses, isLoading: isLoadingCourses } = useCourseApi();
  const { search: searchRpls } = useRplApi();





  const { search: searchAuditLogs, isLoading: isLoadingAuditLogs } = useAuditLogApi();







  const [auditLogs, setAuditLogs] = useState<AuditLogDTO[]>([]);
  const [auditCurrentPage, setAuditCurrentPage] = useState(1);
  const [auditTotalPages, setAuditTotalPages] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(10);

  const isHunterCourse = () => {
    return [
      CourseTypeConstants.DEDICATED_HUNTER,
      CourseTypeConstants.DEDICATED_HUNTER_AND_SPORTS_PERSON,
    ].includes(formData.courseTypeId ?? '');
  };

  const isSportsPersonCourse = () => {
    return [
      CourseTypeConstants.DEDICATED_SPORTS_PERSON,
      CourseTypeConstants.DEDICATED_HUNTER_AND_SPORTS_PERSON,
    ].includes(formData.courseTypeId ?? '');
  };

  const validationRules: ValidationRules = {
    courseTypeId: [{ required: true, message: 'Application Type is required' }],

    ...(roles?.includes('User')
      ? {
        question1_1: [
          {
            custom: (value) => Boolean(value) === true,
            message: 'Question 1.1 is mandatory',
          },
        ],

        question2_1: [
          {
            custom: (value, formData) => {
              if (!isHunterCourse()) return true; // Not required if not hunter course
              return Boolean(value) === true;
            },
            message: 'Question 2.1 is mandatory for Hunter courses',
          },
        ],

        question2_2: [
          {
            custom: (value, formData) => {
              if (!isSportsPersonCourse()) return true; // Not required if not sports person course
              return Boolean(value) === true;
            },
            message: 'Question 2.2 is mandatory for Sports Person courses',
          },
        ],

        question3_group: [
          {
            custom: (value, formData) => {
              const questions3 = [
                formData.question3_1,
                formData.question3_2,
                formData.question3_3,
                formData.question3_4,
                formData.question3_5,
                formData.question3_6,
                formData.question3_7,
              ];
              return questions3.some((q) => q === true);
            },
            message: 'At least one question from 3.1 to 3.7 must be answered',
          },
        ],

        question4_1: [
          {
            custom: (value, formData) => {
              if (!isHunterCourse()) return true; // Not required if not hunter course
              return Boolean(value) === true;
            },
            message: 'Question 4.1 is mandatory for Hunter courses',
          },
        ],

        question4_2: [
          {
            custom: (value, formData) => {
              if (!isHunterCourse()) return true; // Not required if not hunter course
              return Boolean(value) === true;
            },
            message: 'Question 4.2 is mandatory for Hunter courses',
          },
        ],

        question4_3: [
          {
            custom: (value) => Boolean(value) === true,
            message: 'Question 4.3 is mandatory',
          },
        ],

        question4_4: [
          {
            custom: (value) => Boolean(value) === true,
            message: 'Question 4.4 is mandatory',
          },
        ],

        question4_5: [
          {
            custom: (value) => Boolean(value) === true,
            message: 'Question 4.5 is mandatory',
          },
        ],

        question4_6: [
          {
            custom: (value) => Boolean(value) === true,
            message: 'Question 4.6 is mandatory',
          },
        ],
      }
      : {}),
  };

  const { errors, validate } = useFormValidation<RplDTO>(validationRules);

  const [member, setMember] = useState<MemberDTO>();
  const { getById } = useMemberApi();
  const fetchMember = async () => {
    try {
      const response = await getById(memberId ?? '');
      setMember(response?.data);
    } catch (error) {
      console.error('Error fetching member', error);
    }
  };

  const [courseTypes, setCourseTypes] = useState<CourseTypeDTO[]>([]);
  const { data: courseTypesData = [], isLoading: isLoadingCourseTypes } = useCourseTypes();
  const fetchCourseTypes = async () => {
    setCourseTypes(courseTypesData);
  };


  const { data: memberTypesData, isLoading: isLoadingMemberTypes } = useMemberTypes();

  const { data: memberCategoriesData, isLoading: isLoadingMemberCategories } = useMemberCategories();

  const { data: paymentTypesData, isLoading: isLoadingPaymentTypes } = usePaymentTypes();

  const [rplStatuses, setRplStatuses] = useState<RplStatusDTO[]>([]);
  const { data: rplStatusesData = [], isLoading: isLoadingRplStatuses } = useRplStatuses();
  const fetchRplStatuses = async () => {
    setRplStatuses(rplStatusesData);
  };

  useEffect(() => {
    if (memberId) {
      fetchMember();
    }
  }, [memberId]);

  useEffect(() => {
    if (courseTypesData.length > 0) {
      setCourseTypes(courseTypesData);
      setIsCourseTypesFiltered(false);
    }
  }, [courseTypesData]);


  const [isCourseTypesFiltered, setIsCourseTypesFiltered] = useState<boolean>(false);
  const filterCourseTypes = async () => {
    try {
      if (formData.id) {
        return;
      }

      if (member) {
        const courseResponse = await searchCourses({
          page: 1,
          pageSize: 9999,
          filters: {
            memberId: memberId,
            isDeleted: false,
          },
        });

        const rplResponse = await searchRpls({
          page: 1,
          pageSize: 9999,
          filters: {
            memberId: memberId,
            isDeleted: false,
          },
        });

        const existingCourseApplications: CourseDTO[] =
          courseResponse?.data?.results || [];
        const existingRplApplications: RplDTO[] =
          rplResponse?.data?.results || [];

        const existingApplicationTypeIds = [
          ...existingCourseApplications
            .filter((course) => course.courseStatusId !== CourseStatusConstants.DECLINED && course.courseStatusId !== CourseStatusConstants.FORFEITED)
            .map((course) => course.courseTypeId),
          ...existingRplApplications
            .filter((rpl) => rpl.rplStatusId !== RplStatusConstants.DECLINED && rpl.rplStatusId !== RplStatusConstants.DECLINED_WITH_FEEDBACK && rpl.rplStatusId !== RplStatusConstants.FORFEITED)
            .map((rpl) => rpl.courseTypeId),
        ].filter(Boolean);

        const memberCategoryId = member.memberCategoryId;
        let filteredCourseTypes: CourseTypeDTO[] = [];

        const hasHunterApplication = existingApplicationTypeIds.some(
          (courseTypeId) =>
            courseTypeId === CourseTypeConstants.DEDICATED_HUNTER ||
            courseTypeId ===
            CourseTypeConstants.DEDICATED_HUNTER_AND_SPORTS_PERSON,
        );

        const hasSportsPersonApplication = existingApplicationTypeIds.some(
          (courseTypeId) =>
            courseTypeId === CourseTypeConstants.DEDICATED_SPORTS_PERSON ||
            courseTypeId ===
            CourseTypeConstants.DEDICATED_HUNTER_AND_SPORTS_PERSON,
        );

        if (
          memberCategoryId === MemberCategoryConstants.DEDICATED_HUNTER ||
          memberCategoryId ===
          MemberCategoryConstants.DEDICATED_HUNTER_AND_OCCASIONAL_SPORTPERSON ||
          memberCategoryId ===
          MemberCategoryConstants.DEDICATED_HUNTER_AND_SPORTPERSON
        ) {
          filteredCourseTypes = courseTypes.filter(
            (ct) =>
              ct.id === CourseTypeConstants.DEDICATED_SPORTS_PERSON &&
              !hasSportsPersonApplication,
          );
        } else if (
          memberCategoryId === MemberCategoryConstants.DEDICATED_SPORTPERSON ||
          memberCategoryId ===
          MemberCategoryConstants.DEDICATED_SPORTPERSON_AND_OCCASIONAL_HUNTER ||
          memberCategoryId ===
          MemberCategoryConstants.DEDICATED_HUNTER_AND_SPORTPERSON
        ) {
          filteredCourseTypes = courseTypes.filter(
            (ct) =>
              ct.id === CourseTypeConstants.DEDICATED_HUNTER &&
              !hasHunterApplication,
          );
        } else {
          filteredCourseTypes = courseTypes.filter((ct) => {
            if (existingApplicationTypeIds.includes(ct.id)) {
              return false;
            }

            if (
              ct.id === CourseTypeConstants.DEDICATED_HUNTER_AND_SPORTS_PERSON
            ) {
              return !hasHunterApplication && !hasSportsPersonApplication;
            }

            if (ct.id === CourseTypeConstants.DEDICATED_HUNTER) {
              return !hasHunterApplication;
            }

            if (ct.id === CourseTypeConstants.DEDICATED_SPORTS_PERSON) {
              return !hasSportsPersonApplication;
            }

            return true;
          });
        }

        setCourseTypes(filteredCourseTypes);
        setIsCourseTypesFiltered(true);
      }
    } catch (error) {
      console.error('Error filtering course types:', error);
    }
  };

  useEffect(() => {
    if (courseTypes && courseTypes.length > 0 && !isCourseTypesFiltered && member) {
      filterCourseTypes();
    }
  }, [courseTypes, member]);


  useEffect(() => {
    setFormData((prev) => ({
      ...initialValues,
      courseTypeId: prev.courseTypeId,
    }));
  }, [formData.courseTypeId]);

  useEffect(() => {
    if (roles?.includes('Admin') && rplStatusesData.length > 0) {
      setRplStatuses(rplStatusesData);
    }
  }, [roles, rplStatusesData]);


  const handleFileChange = (field: keyof RplDTO) => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        setUploadingFiles((prev) => ({ ...prev, [field]: true }));
        const response = await uploadFile(
          'files',
          file.name,
          file,
          'rpl',
        );
        if (response && response.success && response.data) {
          setFormData((prev) => ({
            ...prev,
            [field]: response.data,
          }));
        }
      } catch (error) {
        console.error('Error handling file:', error);
      } finally { setUploadingFiles((prev) => ({ ...prev, [field]: false })); }
    }
  };

  const handlePopFileChange = (field: keyof RplDTO) => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        setUploadingFiles((prev) => ({ ...prev, [field]: true }));
        const response = await uploadFile(
          'files',
          file.name,
          file,
          'rpl',
        );
        if (response && response.success && response.data) {
          setFormData((prev) => ({
            ...prev,
            [field]: response.data,
          }));
        }
      } catch (error) {
        console.error('Error handling proof of payment file:', error);
      } finally {
        setUploadingFiles((prev) => ({ ...prev, [field]: false }));
      }
    }
  };

  useEffect(() => {
    const fetchAuditLogs = async () => {
      if (initialValues.id) {
        try {
          const response = await searchAuditLogs({
            page: auditCurrentPage,
            pageSize: auditPageSize,
            sortBy: 'CreatedUtc',
            sortDescending: true,
            filters: {
              tableName: 'Rpl',
              recordId: initialValues.id,
            },
          });

          if (response && response?.success && response?.data) {
            setAuditLogs(response.data.results as AuditLogDTO[]);
            setAuditTotalPages(
              Math.ceil((response.data.totalCount ?? 0) / auditPageSize),
            );
          }
        } catch (error) {
          console.error('Error fetching audit logs:', error);
        }
      }
    };

    fetchAuditLogs();
  }, [initialValues.id, auditCurrentPage, auditPageSize]);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form
      sections={sections}
      onSave={handleSave}
      onCancel={onCancel}
      isSubmitting={isLoading}
    >
      {roles?.includes('Admin') && FORM_SECTIONS.memberDetails && (
        <FormSection
          id={FORM_SECTIONS.memberDetails.id}
          title={FORM_SECTIONS.memberDetails.title}
        >
          <MemberDetails member={formData.member ?? {}} />
        </FormSection>
      )}
      {roles?.includes('User') && (
        <FormSection
          id={FORM_SECTIONS.applicationDetails.id}
          title={FORM_SECTIONS.applicationDetails.title}
          columns={1}
        >
          <SimpleAlert
            variant="warning"
            message={
              <>
                Please note! Dedicated status will ONLY be awarded on grounds of RPL (
                <a
                  href="https://www.natshoot.co.za/dedicated-status/#familiarise-yourself-with-the-process-and-requirements-here"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold underline hover:text-yellow-700"
                >
                  view here
                </a>
                ).
                <br /><br />
                Please do NOT complete this application if you have completed the Natshoot (NHSA) course.
                <br /><br />
                For any questions or topics relating to RPL use the help icon at the top right corner.
              </>
            }
          />
          {errors.question3_group && (
            <SimpleAlert variant="error" message={errors.question3_group} />
          )}
          <FormField
            id="courseTypeId"
            label="Application Type"
            type="dropdown"
            value={formData.courseTypeId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select Application Type"
            required={true}
            error={errors.courseTypeId}
            options={courseTypes.map((type) => ({
              value: type.id,
              label: type.title || '',
            }))}
            isLoading={isLoadingCourseTypes}
          />
          {formData.courseTypeId && (
            <>
              <FormField
                id="question1_1"
                label="Question - 1.1"
                type="checkbox"
                subtext="I understand that the process of attaining dedicated status with Natshoot (NHSA) is a legally prescribed process and that it entails strict legally prescribed actions to which both the NHSA and myself have to adhere to with no exceptions, as it has direct bearing on my continued legal possession of firearms."
                value={formData.question1_1 || ''}
                onChange={(e) => handleChange(e, setFormData)}
                required={true}
                error={errors.question1_1}
                helper="I Confirm?"
              />
              {isHunterCourse() && (
                <FormField
                  id="question2_1"
                  label="Question - 2.1"
                  type="checkbox"
                  subtext="I regularly, and in a dedicated manner, participate in Hunting and related activities with my Hunting firearms."
                  value={formData.question2_1 || ''}
                  onChange={(e) => handleChange(e, setFormData)}
                  required={true}
                  error={errors.question2_1}
                  helper="I Confirm?"
                />
              )}
              {isSportsPersonCourse() && (
                <FormField
                  id="question2_2"
                  label="Question - 2.2"
                  type="checkbox"
                  subtext="I regularly, and in a dedicated manner, participate in Sport Shooting and related activities with my Sport Shooting firearms."
                  value={formData.question2_2 || ''}
                  onChange={(e) => handleChange(e, setFormData)}
                  placeholder="Enter Question2_2"
                  required={true}
                  error={errors.question2_2}
                  helper="I Confirm?"
                />
              )}
              {isHunterCourse() && (
                <>
                  <FormField
                    id="question3_1"
                    label="Question - 3.1"
                    type="checkbox"
                    subtext="I was awarded Bona Fide Hunter Status by SAPS under the 1969 Act and apply to be awarded Dedicated Hunter status with Natshoot (NHSA) based on RPL."
                    value={formData.question3_1 || ''}
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Enter Question3_1"
                    required={true}
                    error={errors.question3_1}
                  />

                  {formData.question3_1 === true && (
                    <>
                      {formData.questionFile3_1Id && (
                        <ImageLink
                          fileId={formData.questionFile3_1Id}
                          label="Question 3.1 File"
                        />
                      )}
                      <FileUpload
                        id="questionFile3_1Id"
                        onChange={handleFileChange('questionFile3_1Id')}
                        title="Question 3.1 file"
                        accept={FileTypeConstants.ALL_SUPPORTED}
                        required={true}
                        error={errors.questionFile3_1Id}
                        isUploading={uploadingFiles['questionFile3_1Id']}
                      />
                    </>
                  )}
                </>
              )}
              {isSportsPersonCourse() && (
                <>
                  <FormField
                    id="question3_2"
                    label="Question - 3.2"
                    type="checkbox"
                    subtext="I was awarded Bona Fide Sport-Person Status by SAPS under the 1969 Act and apply to be awarded Dedicated Sport-Person status with Natshoot (NHSA) based on RPL."
                    value={formData.question3_2 || ''}
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Enter Question3_2"
                    required={true}
                    error={errors.question3_2}
                  />
                  {formData.question3_2 === true && (
                    <>
                      {formData.questionFile3_2Id && (
                        <ImageLink
                          fileId={formData.questionFile3_2Id}
                          label="Question 3.2 File"
                        />
                      )}
                      <FileUpload
                        id="questionFile3_2Id"
                        onChange={handleFileChange('questionFile3_2Id')}
                        title="Question 3.2 file"
                        accept={FileTypeConstants.ALL_SUPPORTED}
                        required={true}
                        error={errors.questionFile3_2Id}
                        isUploading={uploadingFiles['questionFile3_2Id']}
                      />
                    </>
                  )}
                </>
              )}
              {isHunterCourse() && (
                <>
                  <FormField
                    id="question3_3"
                    label="Question - 3.3"
                    type="checkbox"
                    subtext="I completed a Professional Hunter's course and apply to be awarded Dedicated Hunter status with Natshoot (NHSA) based on RPL."
                    value={formData.question3_3 || ''}
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Enter Question3_3"
                    required={true}
                    error={errors.question3_3}
                  />
                  {formData.question3_3 === true && (
                    <>
                      {formData.questionFile3_3Id && (
                        <ImageLink
                          fileId={formData.questionFile3_3Id}
                          label="Question 3.3 File"
                        />
                      )}
                      <FileUpload
                        id="questionFile3_3Id"
                        onChange={handleFileChange('questionFile3_3Id')}
                        title="Question 3.3 file"
                        accept={FileTypeConstants.ALL_SUPPORTED}
                        required={true}
                        error={errors.questionFile3_3Id}
                        isUploading={uploadingFiles['questionFile3_3Id']}
                      />
                    </>
                  )}
                  <FormField
                    id="question3_4"
                    label="Question - 3.4"
                    type="checkbox"
                    subtext="I completed the CHASA Proficiency Award Test and apply to be awarded Dedicated Hunter status with Natshoot (NHSA) based on RPL."
                    value={formData.question3_4 || ''}
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Enter Question3_4"
                    required={true}
                    error={errors.question3_4}
                  />
                  {formData.question3_4 === true && (
                    <>
                      {formData.questionFile3_4Id && (
                        <ImageLink
                          fileId={formData.questionFile3_4Id}
                          label="Question 3.4 File"
                        />
                      )}
                      <FileUpload
                        id="questionFile3_4Id"
                        onChange={handleFileChange('questionFile3_4Id')}
                        title="Question 3.4 file"
                        accept={FileTypeConstants.ALL_SUPPORTED}
                        required={true}
                        error={errors.questionFile3_4Id}
                        isUploading={uploadingFiles['questionFile3_4Id']}
                      />
                    </>
                  )}
                  <FormField
                    id="question3_5"
                    label="Question - 3.5"
                    type="checkbox"
                    subtext="I hold current and up to date Dedicated Hunter Status with another accredited Hunting Association and apply to be awarded Dedicated Hunter status with Natshoot (NHSA) based on RPL."
                    value={formData.question3_5 || ''}
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Enter Question3_5"
                    required={true}
                    error={errors.question3_5}
                  />
                  {formData.question3_5 === true && (
                    <>
                      {formData.questionFile3_5Id && (
                        <ImageLink
                          fileId={formData.questionFile3_5Id}
                          label="Question 3.5 File"
                        />
                      )}
                      <FileUpload
                        id="questionFile3_5Id"
                        onChange={handleFileChange('questionFile3_5Id')}
                        title="Question 3.5 file"
                        accept={FileTypeConstants.ALL_SUPPORTED}
                        required={true}
                        error={errors.questionFile3_5Id}
                        isUploading={uploadingFiles['questionFile3_5Id']}
                      />
                    </>
                  )}
                </>
              )}
              {isSportsPersonCourse() && (
                <>
                  <FormField
                    id="question3_6"
                    label="Question - 3.6"
                    type="checkbox"
                    subtext="I hold current and up to date Dedicated Sport Person Status with another accredited Sport Shooting Association and apply to be awarded Dedicated Sport-Person status with Natshoot (NHSA) based on RPL."
                    value={formData.question3_6 || ''}
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Enter Question3_6"
                    required={true}
                    error={errors.question3_6}
                  />
                  {formData.question3_6 === true && (
                    <>
                      {formData.questionFile3_6Id && (
                        <ImageLink
                          fileId={formData.questionFile3_6Id}
                          label="Question 3.6 File"
                        />
                      )}
                      <FileUpload
                        id="questionFile3_6Id"
                        onChange={handleFileChange('questionFile3_6Id')}
                        title="Question 3.6 file"
                        accept={FileTypeConstants.ALL_SUPPORTED}
                        required={true}
                        error={errors.questionFile3_6Id}
                        isUploading={uploadingFiles['questionFile3_6Id']}
                      />
                    </>
                  )}
                </>
              )}
              {isHunterCourse() && (
                <>
                  <FormField
                    id="question3_7"
                    label="Question - 3.7"
                    type="checkbox"
                    subtext="I have completed the Hunters Training Course in an European Country or in any State in the USA, and apply to be awarded Dedicated Hunter status with Natshoot (NHSA) based on RPL."
                    value={formData.question3_7 || ''}
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Enter Question3_7"
                    required={true}
                    error={errors.question3_7}
                  />
                  {formData.question3_7 === true && (
                    <>
                      {formData.questionFile3_7Id && (
                        <ImageLink
                          fileId={formData.questionFile3_7Id}
                          label="Question 3.7 File"
                        />
                      )}
                      <FileUpload
                        id="questionFile3_7Id"
                        onChange={handleFileChange('questionFile3_7Id')}
                        title="Question 3.7 file"
                        accept={FileTypeConstants.ALL_SUPPORTED}
                        required={true}
                        error={errors.questionFile3_7Id}
                        isUploading={uploadingFiles['questionFile3_7Id']}
                      />
                    </>
                  )}
                  <FormField
                    id="question4_1"
                    label="Question - 4.1"
                    type="checkbox"
                    subtext="I know and understand that in terms of the FCA (Act 60 of 2000) and its Regulations (2004), I may not conduct any PH related work with the firearms I have licenced based on my Dedicated Hunter Status with Natshoot (NHSA)."
                    value={formData.question4_1 || ''}
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Enter Question4_1"
                    required={true}
                    error={errors.question4_1}
                    helper="I Confirm?"
                  />
                  <FormField
                    id="question4_2"
                    label="Question - 4.2"
                    type="checkbox"
                    subtext="I understand that Dedicated Hunter Status with Natshoot (NHSA) does not include any activity which would entitle me to apply for a NHSA endorsement for a licence application for a semi-automatic rifle/carbine, which is typically used for Sports-Shooting, despite the fact that I may use a legally licenced semi-automatic firearm to participate in NHSA postal target shooting events & competitions."
                    value={formData.question4_2 || ''}
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Enter Question4_2"
                    required={true}
                    error={errors.question4_2}
                    helper="I Confirm?"
                  />
                </>
              )}
              <FormField
                id="question4_3"
                label="Question - 4.3"
                type="checkbox"
                subtext="I understand that it is entirely my own responsibility to annually conduct the necessary activities as accepted by Natshoot (NHSA) to maintain my Dedicated Hunter and/or Sport-Person Status with NHSA."
                value={formData.question4_3 || ''}
                onChange={(e) => handleChange(e, setFormData)}
                placeholder="Enter Question4_3"
                required={true}
                error={errors.question4_3}
                helper="I Confirm?"
              />
              <FormField
                id="question4_4"
                label="Question - 4.4"
                type="checkbox"
                subtext="I understand that if I do not submit my annual Dedicated Hunter and or Sport Shooting report to Natshoot (NHSA) on or before the last day of my birthday month annually, that the NHSA has to inform the SAPS (CFR) of the fact that I have not submitted an annual report and that I would then have forfeited my Dedicated Hunter and/or Sport-Person status with the NHSA due to my own actions or lack thereof. I also understand that it then remains my responsibility to complete the necessary SAPS 521(c) (Change of Circumstances Form) and to submit it to my DFO, after having been informed of the lapse of my dedicated status with NHSA."
                value={formData.question4_4 || ''}
                onChange={(e) => handleChange(e, setFormData)}
                placeholder="Enter Question4_4"
                required={true}
                error={errors.question4_4}
                helper="I Confirm?"
              />
              <FormField
                id="question4_5"
                label="Question - 4.5"
                type="checkbox"
                subtext="I understand that I may submit a non-active report for a specific year (with reasons) and that my Dedicated Hunter and/or Sport-Person status will not be in jeopardy because of that, but that I must furnish good reasons why I for a second consecutive year return a non-active report."
                value={formData.question4_5 || ''}
                onChange={(e) => handleChange(e, setFormData)}
                placeholder="Enter Question4_5"
                required={true}
                error={errors.question4_5}
                helper="I Confirm?"
              />
              <FormField
                id="question4_6"
                label="Question - 4.6"
                type="checkbox"
                subtext="I understand that the maintenance of my dedicated status with Natshoot (NHSA) is entirely my own responsibility, and that I have no claim against the NHSA, any of it's affiliates or associates or any of it's personnel or officers whatsoever, should I loose my dedicated status because of non-compliance with any of the above by Law prescribed processes due to my actions or the lack thereof."
                value={formData.question4_6 || ''}
                onChange={(e) => handleChange(e, setFormData)}
                placeholder="Enter Question4_6"
                required={true}
                error={errors.question4_6}
                helper="I Confirm?"
              />

              {roles?.includes('Admin') && <></>}
            </>
          )}
        </FormSection>
      )}

      {roles?.includes('Admin') && FORM_SECTIONS.userDetails && (
        <FormSection
          id={FORM_SECTIONS.userDetails.id}
          title={FORM_SECTIONS.userDetails.title}
        >
          <FormField
            id="membershipId"
            label={`Membership of ${formData.member?.firstName}`}
            type="dropdown"
            value={formData.member?.memberTypeId || ''}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                member: {
                  ...prev.member,
                  memberTypeId: e.target.value,
                },
              }));
            }}
            placeholder="Select Membership"
            required={true}
            error={errors.rplStatusId}
            options={(memberTypesData || []).map((status) => ({
              value: status.id,
              label: status.title,
            }))}
            isLoading={isLoadingMemberTypes}
          />
          <FormField
            id="membercategoryId"
            label={`FCA status of ${formData.member?.firstName}`}
            type="dropdown"
            value={formData.member?.memberCategoryId || ''}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                member: {
                  ...prev.member,
                  memberCategoryId: e.target.value,
                },
              }));
            }}
            placeholder="Select Membership"
            required={true}
            error={errors.rplStatusId}
            options={(memberCategoriesData || []).map((status) => ({
              value: status.id,
              label: status.title,
            }))}
            isLoading={isLoadingMemberCategories}
          />
          <FormField
            id="dateCertificateHunter"
            label="Certificate Date (Hunter)"
            type="date"
            value={formatDateForInput(formData.member?.dateCertificateHunter)}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                member: {
                  ...prev.member,
                  dateCertificateHunter: new Date(e.target.value),
                },
              }));
            }}
            placeholder="Enter Hunter Certificate Date"
            required={false}
          />
          <FormField
            id="dateCertificateSport"
            label="Certificate Date (Sport)"
            type="date"
            value={formatDateForInput(formData.member?.dateCertificateSport)}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                member: {
                  ...prev.member,
                  dateCertificateSport: new Date(e.target.value),
                },
              }));
            }}
            placeholder="Enter Sport Certificate Date"
            required={false}
          />
        </FormSection>
      )}

      {roles?.includes('Admin') && FORM_SECTIONS.recordDetails && (
        <FormSection
          id={FORM_SECTIONS.recordDetails.id}
          title={FORM_SECTIONS.recordDetails.title}
        >
          <FormField
            id="notes"
            label="Notes"
            type="textarea"
            value={formData.notes || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Notes"
            required={false}
            error={errors.notes}
          />
        </FormSection>
      )}

      {roles?.includes('Admin') && FORM_SECTIONS.paymentDetails && (
        <FormSection
          id={FORM_SECTIONS.paymentDetails.id}
          title={FORM_SECTIONS.paymentDetails.title}
        >
          <FormField
            id="paymentTypeId"
            label="Payment Type"
            type="dropdown"
            value={formData.paymentTypeId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select Payment Type"
            required={false}
            error={errors.paymentTypeId}
            options={(paymentTypesData || []).map((status) => ({
              value: status.id,
              label: status.title,
            }))}
            isLoading={isLoadingPaymentTypes}
          />
          {formData.proofOfPaymentFileId && (
            <ImageLink
              fileId={formData.proofOfPaymentFileId}
              label="Proof of payment"
            />
          )}
          <FileUpload
            id="proofOfPaymentFile"
            onChange={handlePopFileChange('proofOfPaymentFileId')}
            title="Proof of payment"
            accept={FileTypeConstants.ALL_SUPPORTED}
            required={false}
            error={errors.proofOfPaymentFile}
            isUploading={uploadingFiles['proofOfPaymentFileId']}
          />
          <FormField
            id="amountPaid"
            label="Amount Paid"
            type="number"
            value={formData.amountPaid || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Amount Paid"
            required={false}
            error={errors.amountPaid}
          />
          <FormField
            id="paidDate"
            label="Paid Date"
            type="date"
            value={formData.paidDate || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Paid Date"
            required={false}
            error={errors.paidDate}
          />
        </FormSection>
      )}

      {roles?.includes('Admin') && FORM_SECTIONS.feedback && (
        <FormSection
          id={FORM_SECTIONS.feedback.id}
          title={FORM_SECTIONS.feedback.title}
        >
          <FeedbackForm
            componentId={ComponentConstants.RPL}
            recordId={formData.id ?? ''}
            initialData={formData}
            componentStatuses={rplStatuses.map((status) => ({
              value: status.id,
              label: status.title,
            }))}
            statusFieldName={statusFieldName}
            onChange={(updatedData) => {
              setFormData((prev) => ({
                ...prev,
                ...updatedData,
              }));
            }}
          />
        </FormSection>
      )}

      {roles?.includes('Admin') && FORM_SECTIONS.history && (
        <FormSection
          id={FORM_SECTIONS.history.id}
          title={FORM_SECTIONS.history.title}
        >
          <AuditLogTable
            data={auditLogs}
            currentPage={auditCurrentPage}
            totalPages={auditTotalPages}
            setCurrentPage={setAuditCurrentPage}
            pageSize={auditPageSize}
            setPageSize={setAuditPageSize}
            isLoading={isLoadingAuditLogs}
          />
        </FormSection>
      )}
    </Form>
  );
};

export default RplForm;
